import React, { useEffect, useState } from 'react';
import axiosAPIinstance from '../../config/axiosAPIinstance';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Box, Typography,
  TextField, InputAdornment, MenuItem, FormControl,
  Select, Grid, Divider, Chip, Button, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { 
  Edit, Delete, Search, FilterList, 
  Clear, Check, Close 
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import BackButton from '../BackButton';

const CategorysList = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('');
  const [parentFilter, setParentFilter] = useState('');
  const [priorityRange, setPriorityRange] = useState([null, null]);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axiosAPIinstance.get('/categories');
        setCategories(data);
        setFilteredCategories(data);
      } catch (error) {
        alert('Error fetching categories: ' + error.message);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [categories, searchTerm, statusFilter, featuredFilter, parentFilter, priorityRange]);

  const applyFilters = () => {
    let result = [...categories];

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(category => 
        category.name?.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter) {
      result = result.filter(category => category.status === statusFilter);
    }

    // Featured filter
    if (featuredFilter !== '') {
      result = result.filter(category => 
        featuredFilter === 'true' ? category.featured : !category.featured
      );
    }

    // Parent category filter - FIXED
    if (parentFilter) {
      if (parentFilter === 'null') {
        result = result.filter(category => !category.parentCategoryId);
      } else {
        const filterId = parentFilter.toString();
        result = result.filter(category => 
          category.parentCategoryId?.toString() === filterId
        );
      }
    }

    // Priority range filter
    if (priorityRange[0] !== null || priorityRange[1] !== null) {
      result = result.filter(category => {
        const priority = category.priority || 0;
        return (priorityRange[0] === null || priority >= priorityRange[0]) &&
               (priorityRange[1] === null || priority <= priorityRange[1]);
      });
    }

    setFilteredCategories(result);
  };

  // FIXED: Improved parent options function
  const getParentOptions = () => {
    const parentIds = [...new Set(categories.map(c => c.parentCategoryId).filter(id => id != null))];
    
    return parentIds.map(parentId => {
      const parentCategory = categories.find(c => c.id === parentId);
      return {
        id: parentId,
        name: parentCategory ? parentCategory.name : `Parent ID: ${parentId}`
      };
    });
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setFeaturedFilter('');
    setParentFilter('');
    setPriorityRange([null, null]);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      await axiosAPIinstance.delete(`/categories/${categoryToDelete.id}`);
      setCategories(categories.filter(cat => cat.id !== categoryToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      if (error.response && error.response.status === 409) {
        alert('Cannot delete this category because it has subcategories. Please delete or move the subcategories first.');
      } else {
        alert('Error deleting category: ' + error.message);
      }
    }
  };

  const getStatusOptions = () => {
    const statuses = categories.map(c => c.status).filter(Boolean);
    return [...new Set(statuses)];
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 4, md: 10 }, py: { xs: 2, sm: 4 } }}>
      <Typography variant="h4" gutterBottom align="center">
        Category Management
      </Typography>

      {/* Search and Filter Section */}
      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mr: 2 }}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={resetFilters}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {showFilters && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom>Status</Typography>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    {getStatusOptions().map(status => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom>Featured</Typography>
                  <Select
                    value={featuredFilter}
                    onChange={(e) => setFeaturedFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Featured</MenuItem>
                    <MenuItem value="false">Not Featured</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom>Parent Category</Typography>
                  <Select
                    value={parentFilter}
                    onChange={(e) => setParentFilter(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    <MenuItem value="null">Root Categories</MenuItem>
                    {getParentOptions().map(parent => (
                      <MenuItem key={parent.id} value={parent.id}>{parent.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom>Priority Range</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        type="number"
                        placeholder="Min"
                        value={priorityRange[0] || ''}
                        onChange={(e) => setPriorityRange([
                          e.target.value !== '' ? parseInt(e.target.value) : null,
                          priorityRange[1]
                        ])}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        type="number"
                        placeholder="Max"
                        value={priorityRange[1] || ''}
                        onChange={(e) => setPriorityRange([
                          priorityRange[0],
                          e.target.value !== '' ? parseInt(e.target.value) : null
                        ])}
                      />
                    </Grid>
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={`Showing ${filteredCategories.length} of ${categories.length} categories`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Categories Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: 'primary.light' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Parent</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Featured</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell>{category.id}</TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>{category.name}</TableCell>
                  <TableCell>
                    {category.parentCategoryId ? 
                      categories.find(c => c.id === category.parentCategoryId)?.name || 
                      category.parentCategoryId : 
                      '—'}
                  </TableCell>
                  <TableCell>
                    {category.imageUrl && (
                      <Avatar 
                        src={category.imageUrl} 
                        alt={category.name}
                        sx={{ width: 50, height: 50 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{category.priority}</TableCell>
                  <TableCell>
                    <Chip 
                      label={category.status} 
                      color={
                        category.status === 'Active' ? 'success' : 
                        category.status === 'Inactive' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {category.featured ? (
                      <Check color="success" />
                    ) : (
                      <Close color="error" />
                    )}
                  </TableCell>
                  <TableCell sx={{ 
                    maxWidth: 200,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {category.description}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      component={Link} 
                      to="/categoryForm" 
                      state={{ categoryId: category.id }}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteClick(category)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1">No categories found matching your criteria</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <div className='divFlexSpan'>
          <BackButton />
        </div>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {categoryToDelete && (
            <Typography>
              Are you sure you want to delete the category "{categoryToDelete.name}"?
              {categoryToDelete.subCategories?.length > 0 && (
                <Typography color="error" sx={{ mt: 1 }}>
                  Warning: This category has subcategories!
                </Typography>
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategorysList;